// Per-rule matching for `event_chat` trigger rules.
//
// The generic matcher in `TriggerEngine.fireEvent` only does exact
// string-equality between `rule.eventConfig[key]` and `payload[key]`,
// which can't express "message contains X" / "author has badge Y".
// This module implements the chat-specific semantics; the engine
// delegates here when `rule.event === "event_chat"`.
//
// Supported eventConfig keys (all optional — an empty/missing key, or
// the `__all__` wildcard used elsewhere in the rule editor, matches
// everything):
//
//   textMatch     — case-insensitive substring of the message text
//   isRegex       — "true" → treat `textMatch` as a regular expression
//                   (case-insensitive); invalid patterns never match
//   platform      — "twitch" | "youtube" | "kick" (case-insensitive)
//   userName      — exact author name, case-insensitive (leading "@" ok)
//   requiredBadge — author must carry this badge ("moderator", "vip",
//                   "subscriber", "broadcaster", ...). Matches badge
//                   entries exactly or as a "badge/version" prefix.

interface ChatLikePayload {
  platform?: unknown;
  authorName?: unknown;
  authorBadges?: unknown;
  text?: unknown;
}

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const isWildcard = (value: string | undefined): boolean =>
  !value || value.trim() === "" || value === "__all__";

const badgeMatches = (badges: unknown, required: string): boolean => {
  if (!Array.isArray(badges)) return false;

  const wanted = required.trim().toLowerCase();

  return badges.some((badge) => {
    if (typeof badge !== "string") return false;

    const normalized = badge.trim().toLowerCase();

    return normalized === wanted || normalized.startsWith(`${wanted}/`);
  });
};

export const matchesChatEvent = (
  eventConfig: Record<string, string>,
  payload: unknown,
): boolean => {
  const chat = (payload ?? {}) as ChatLikePayload;

  // Platform filter
  const platform = eventConfig.platform;

  if (!isWildcard(platform)) {
    const actual = asString(chat.platform).trim().toLowerCase();

    if (actual !== platform.trim().toLowerCase()) return false;
  }

  // Author filter (tolerate a leading "@" in the configured name)
  const userName = eventConfig.userName;

  if (!isWildcard(userName)) {
    const wanted = userName.trim().replace(/^@/, "").toLowerCase();

    const actual = asString(chat.authorName).trim().toLowerCase();

    if (actual !== wanted) return false;
  }

  // Badge filter
  const requiredBadge = eventConfig.requiredBadge;

  if (!isWildcard(requiredBadge)) {
    if (!badgeMatches(chat.authorBadges, requiredBadge)) return false;
  }

  // Text filter (substring by default, regex when isRegex === "true")
  const textMatch = eventConfig.textMatch;

  if (textMatch && textMatch.trim() !== "") {
    const text = asString(chat.text);

    if (eventConfig.isRegex === "true") {
      try {
        if (!new RegExp(textMatch, "i").test(text)) return false;
      } catch {
        // Invalid user-provided pattern: never match (visible no-op is
        // safer than throwing inside the engine loop).
        return false;
      }
    } else if (!text.toLowerCase().includes(textMatch.trim().toLowerCase())) {
      return false;
    }
  }

  return true;
};
